import { resultCode, version, resultMessage, wrapResult, athenaJsUrl, athenaCssUrl } from '../module/entity';
import { sendRequestService } from '../module/cgi';
import { setServerEnv as setHttpServerEnv } from '../module/request';
import { platform, verCompare, loadScript, loadStyle } from '../module/util';
import { osVersion, appVersion, isTenvideoPhone } from '../module/ua';
import doReport from '../module/report';
import { invokeAsync } from '../module/bridge';
import audioInstance from '@iv/shared/src/audio';
import { $on, $off, $once, Events } from './common-event';
import VideoClip from './ui/video-clip';
import { log } from '../../log';
import { isUrl } from '@iv/shared/src/url';

// 加载音频
export async function loadSound(soundInfo:
| {
  id: string;
  src: string;
}
| string): Promise<IResult> {
  let soundId;
  if (typeof soundInfo === 'object') {
    audioInstance.loadFile(soundInfo);
    const randomNumber = Math.random();
    soundId = soundInfo.id || randomNumber.toString(36).substr(2, 4);
  } else if (typeof soundInfo === 'string') {
    soundId = Math.random()
      .toString(36)
      .substr(2, 4);
    audioInstance.loadFile({
      id: soundId,
      src: soundInfo,
    });
  }
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {
      id: soundId,
    },
  });
}

// 播放音频
export async function playSound(playInfo: {
  id: string;
  loop: boolean;
  time: number;
}): Promise<IResult> {
  audioInstance.play(playInfo);
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {},
  });
}

// 暂停音频
export async function stopSound(id: string): Promise<IResult> {
  audioInstance.stop(id);
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {},
  });
}

// 销毁音频
export async function destroySound(id: string): Promise<IResult> {
  audioInstance.end(id);
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {},
  });
}

// 销毁全部音频
export async function destroyAllSound(): Promise<IResult> {
  audioInstance.endAll();
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {},
  });
}

// 事件监听
export async function on(event: Events, callback: (res?: any) => any) {
  if (event in Events) {
    $on(event, callback);
  }
}

// 只监听一次事件
export async function once(event: Events, callback: (res?: any) => any) {
  if (event in Events) {
    $once(event, callback);
  }
}

// 事件取消监听
export async function off(event: Events, callback?: (res?: any) => any) {
  if (event in Events) {
    $off(event, callback);
  }
}

// 获取版本
export async function getVersion(): Promise<IResult> {
  return new Promise((resolve) => {
    resolve({
      code: resultCode.ok,
      msg: resultMessage.ok,
      result: {
        sdkVersion: version,
        appVersion,
        osVersion,
      },
    });
  });
}

// 获取当前运行平台
export async function getPlatform(): Promise<IResult> {
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {
      platform,
    },
  });
}

// 切换环境
export async function setServerEnv(param: {
  isTest: false;
  isPreview: false;
  session: '';
}): Promise<IResult> {
  const result = await setHttpServerEnv(param);
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result,
  });
}

// 版本比较
export async function compareAppVer(param: { v1: string; v2: string }): Promise<IResult> {
  const { v1, v2 } = param;
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: verCompare(v1, v2),
  });
}

// 复制到粘贴板
export async function copyToClipBoard(param: { content: string }): Promise<IResult> {
  if (isTenvideoPhone) {
    const result = await invokeAsync('copyToClipBoard', param);
    const { errCode, errMsg } = result;
    if (errCode === 0 || errMsg.indexOf('success') > -1) {
      return wrapResult(resultCode.ok, resultMessage.ok);
    }
    return wrapResult(resultCode.error, `${resultMessage.error}: ${errMsg}(${errCode})`);
  }
  try {
    const input = document.createElement('input');
    input.style.display = 'none';
    input.value = param.content;
    input.select();
    document.execCommand('copy');
    return wrapResult(resultCode.ok, resultMessage.ok);
  } catch (e) {
    return wrapResult(resultCode.error, `${resultMessage.error}: ${e.message}`);
  }
}

let globalCid: string;
// 上报
export async function report(param: { type: string; data: any }): Promise<IResult> {
  const reportData = param;
  const cid = reportData?.data?.cid || globalCid;
  if (!cid) {
    const res = await sendRequestService({
      service: 'queryAppCid',
      data: {
        appid: reportData.data.appid,
      },
      isTest: false,
    });
    globalCid = res.result.cid;
    reportData.data.cid = res.result.cid;
  }
  reportData.data.platform = platform;

  doReport(reportData);
  return Promise.resolve({
    code: 0,
    msg: 'ok',
    result: {},
  });
}

// 请求服务接口
export async function requestService(param: {
  service: any;
  data: any;
  isTest: boolean;
}): Promise<IResult> {
  return sendRequestService(param);
}

// 滚动
export async function scrollTo(param: { left: number; top: number }): Promise<IResult> {
  window.scroll(param.left || 0, param.top || 0);
  return Promise.resolve({ code: 0, msg: 'ok', result: {} });
}

// 获取图片的base64字符串
export async function getImageBase64String(param: { url: string }): Promise<IResult> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const url = param.url.replace(/^http:/, 'https:');
    const getResult = (url: string | ArrayBuffer, err?: Error | DOMException) => ({
      code: err ? -1 : 0,
      msg: err ? err.message : 'ok',
      result: {
        url: url.toString(),
      },
    });
    xhr.addEventListener('load', () => {
      if (xhr.status === 200 && xhr.readyState === 4) {
        const reader = new FileReader();
        reader.addEventListener('loadend', () => {
          resolve(getResult(reader.result));
        });
        reader.addEventListener('error', (event) => {
          resolve(getResult(reader.result, event.target.error));
        });
        reader.readAsDataURL(xhr.response);
      }
    });
    xhr.addEventListener('error', () => {
      resolve(getResult('', new Error(xhr.responseText)));
    });
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
}

// 刷新
export async function reload(isForce = false) {
  window.top.location.reload(isForce);
}

// 重定向
export async function redirect(url = '') {
  if (!isUrl(url)) return;
  window.top.location.href = url;
}

// 拉取道具列表
export async function getProductList({
  dramaId,
  vid = '',
  isTest = false,
}: {
  dramaId: string,
  vid?: string,
  isTest?: boolean,
}): Promise<IResult> {
  return sendRequestService({
    service: 'getProductList',
    data: {
      drama_id: dramaId,
      vid,
    },
    isTest,
  });
}

// 单点支付
export async function pay(param: {
  dramaId: string,
  productId: string,
  isTest?: boolean
}): Promise<IResult> {
  const { dramaId, productId, isTest } = param;
  const res =  await sendRequestService({
    service: 'pay',
    data: {
      drama_id: dramaId,
      product_id: productId,
      product_num: '1',
      timestamp: `${Math.floor(+new Date() / 1000)}`,
      reqtime: `${Math.floor(+new Date() / 1000)}`,
      pf: '2',
    },
    isTest: !!isTest,
  });
  log('[pay step1]: ', res);

  if (res.code === 0) {
    if (!window.VftSingleCashier) {
      await loadScript('//vm.gtimg.cn/tencentvideo_v1/3party/zepto.js');
      await loadScript('//vm.gtimg.cn/tencentvideo/script/film/lib/VftSingleCashier.umd.min.js');
    }
    const { payRemark } = res.result;
    return new Promise((resolve) => {
      window.VftSingleCashier.buy({
        appid: '1008', // 必填
        items: `${productId}:1`, // 必填
        remark: payRemark, // 必填
        onSuccess(res: any) {
          log('[pay step2]: ', res);
          resolve({
            code: 0,
            msg: 'pay success',
          });
        },
        onCancel() {
          log('[pay step2]: user canceled...');
          resolve({
            code: -1,
            msg: 'user canceled...',
          });
        },
      });
    });
  }
  return res;
}

// 查询支付状态
export async function getPayState(param: {
  dramaId: string,
  productId?: string,
  isTest?: boolean
}): Promise<IResult> {
  const { dramaId, productId, isTest } = param;
  return sendRequestService({
    service: 'getPayState',
    data: {
      drama_id: dramaId,
      product_id: productId,
    },
    isTest,
  });
}

// 读取kv值
export async function getString({ keys, dramaId }: {keys: Array<string>, dramaId: string}) {
  const res = await sendRequestService({
    service: 'getTextValue',
    data: {
      drama_id: dramaId,
      text_keys: keys,
    },
  });
  // 未登录时从 localStorage 取值
  if (res.code === 6 && res.msg === 'no login') {
    const result: {[key: string]: string} = {};
    keys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        result[key] = value;
      }
    });
    log('no login, getString from localStorage', result);
    return {
      code: 0,
      msg: 'ok',
      result,
    };
  }
  return res;
}

// 存储kv值
export async function setString({
  kv,
  dramaId,
}: {
  kv: { key: string; value: string },
  dramaId: string,
}) {
  const { key, value } = kv;
  const res = await sendRequestService({
    service: 'setTextValue',
    data: {
      drama_id: dramaId,
      text_key: key,
      text_value: value,
    },
  });
  // 未登录时往 localStorage 存值
  if (res.code === 6 && res.msg === 'no login') {
    localStorage.setItem(key, value);
    log('no login, setString to localStorage', kv);
    return {
      code: 0,
      msg: 'ok',
    };
  }
  return res;
}

// 游戏预约
export async function orderGame(param: {
  type: string,
  giftId: string,
  gameId: string,
  gameName: string,
  gameIcon: string,
}) {
  if (!window.athena) {
    await Promise.all([loadStyle(athenaCssUrl), loadScript(athenaJsUrl)]);
  }
  window.athena.api.origin = 'https://iwan.qq.com';
  const { giftId, gameId, gameName, gameIcon, type } = param;
  const Order = window.athena.OrderBox;

  if (Order[type]) {
    const order = new Order[type]('body', {
      giftId,
      gameInfo: {
        gameId,
        gameName,
        gameIcon,
      },
    });
    return new Promise((resolve) => {
      order.on('order-end', (res: any) => {
        log('[orderGame] 预约结束:', res);
        resolve(wrapResult(resultCode.ok, resultMessage.ok, res.data));
      });
    });
  }
  return wrapResult(resultCode.notSupport, resultMessage.notSupport);
}

// 游戏下载
export async function downloadGame(param: {
  gameInfo: {
    gameId: string,
    qqGameAppId: string,
    android: object,
    ios: object,
  },
  extra: {
    reportParams: string
  }
}) {
  log('download start...');
  if (!window.athena) {
    await Promise.all([loadStyle(athenaCssUrl), loadScript(athenaJsUrl)]);
  }
  window.athena.api.origin = 'https://iwan.qq.com';
  const { downloadDispatch } = window.athena;
  const dispatch = downloadDispatch.getInstance();

  dispatch.add([param]);

  dispatch.get(param.gameInfo.gameId).run(); // 下载

  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 获取排行榜接口
export async function getRankList(param: {
  metaId: string,
  rankId: string,
  pageNum?: number,
  pageSize?: number,
  isTest?: boolean
}): Promise<IResult> {
  const { metaId, rankId, pageNum, pageSize, isTest } = param;
  return sendRequestService({
    service: 'getRankList',
    data: {
      metaId,
      rankId,
      pageNum: pageNum || 1,
      pageSize: pageSize || 10,
    },
    isTest,
  });
}

// 展示插播 H5
export async function showClip(data: {
  param: {
    vid: string,
    x: number,
    y: number,
    width: number,
    height: number,
    hideCloseBtn?: boolean,
  },
  containerId: string,
}): Promise<IResult> {
  log('showClip run..');
  const videoClip = VideoClip.getInstance(data.containerId);
  videoClip.showClip(data.param);

  return new Promise((resolve) => {
    videoClip.once('hideClip', () => {
      resolve(wrapResult(resultCode.ok, resultMessage.ok));
    });
  });
}

// 隐藏插播 H5
export function hideClip(containerId: string) {
  const videoClip = VideoClip.getInstance(containerId);
  videoClip.hideClip();
  return wrapResult(resultCode.ok, resultMessage.ok);
}
